// Login page component
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import LoginForm from '../components/auth/LoginForm';
import RegisterForm from '../components/auth/RegisterForm';
import ForgotPasswordForm from '../components/auth/ForgotPasswordForm';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import { useLanguage } from '../contexts/LanguageContext';
import './LoginPage.css';

function LoginPage() {
  const [searchParams] = useSearchParams();
  const initialForm = searchParams.get('mode') === 'register' ? 'register' : 'login';
  const [currentForm, setCurrentForm] = useState(initialForm); // 'login', 'register', 'forgot'
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  // Update form when URL params change
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'register') {
      setCurrentForm('register');
    } else {
      setCurrentForm('login');
    }
  }, [searchParams]);

  const handleAuthSuccess = () => {
    console.log('Auth success - location state:', location.state);
    
    // Handle both string and object formats for 'from'
    let redirectPath = '/diary'; // default
    
    if (location.state?.from) {
      if (typeof location.state.from === 'string') {
        redirectPath = location.state.from;
      } else if (location.state.from.pathname) {
        redirectPath = location.state.from.pathname;
      }
    }
    
    console.log('Redirecting to:', redirectPath);
    
    // Small delay to ensure auth state is updated
    setTimeout(() => {
      navigate(redirectPath, { replace: true });
    }, 200);
  };

  const handleSwitchToRegister = () => {
    setCurrentForm('register');
    navigate('/login?mode=register', { replace: true });
  };

  const handleSwitchToLogin = () => {
    setCurrentForm('login');
    navigate('/login', { replace: true });
  };

  const renderForm = () => {
    switch (currentForm) {
      case 'register':
        return (
          <RegisterForm
            onSuccess={handleAuthSuccess}
            onSwitchToLogin={handleSwitchToLogin}
          />
        );
      case 'forgot':
        return (
          <ForgotPasswordForm
            onSwitchToLogin={handleSwitchToLogin}
          />
        );
      default:
        return (
          <LoginForm
            onSuccess={handleAuthSuccess}
            onSwitchToRegister={handleSwitchToRegister}
            onSwitchToForgotPassword={() => setCurrentForm('forgot')}
          />
        );
    }
  };

  return (
    <ProtectedRoute requireAuth={false}>
      <div className="login-page">
        <div className="login-welcome-content">
          <div className="login-decoration">
            <img 
              src="/images/tarot-cards/image_21.jpg" 
              alt="Tarot Card" 
              className="login-tarot-image"
            />
            
            {/* USP Bullet Points for Registration */}
            {currentForm === 'register' && (
                <div className="login-usp-section">
                  <h3 className="login-welcome-message">{t('auth.welcome')}</h3>
                  <ul className="login-usp-list">
                    <li className="login-usp-item">
                      <span className="login-usp-icon">🔮</span>
                      <span className="login-usp-text">
                        <strong>{t('auth.uspDrawTarot')}</strong>
                      </span>
                    </li>
                    <li className="login-usp-item">
                      <span className="login-usp-icon">📚</span>
                      <span className="login-usp-text">
                        <strong>{t('auth.uspTarotLibrary')}</strong>
                      </span>
                    </li>
                    <li className="login-usp-item">
                      <span className="login-usp-icon">✨</span>
                      <span className="login-usp-text">
                        <strong>{t('auth.uspGuidedSpreads')}</strong>
                      </span>
                    </li>
                    <li className="login-usp-item">
                      <span className="login-usp-icon">📖</span>
                      <span className="login-usp-text">
                        <strong>{t('auth.uspPersonalDiary')}</strong>
                      </span>
                    </li>
                    <li className="login-usp-item">
                      <span className="login-usp-icon">💡</span>
                      <span className="login-usp-text">
                        <strong>{t('auth.uspInsights')}</strong>
                      </span>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          <div className="login-form-section">
            {renderForm()}
          </div>
          </div>
        </div>
    </ProtectedRoute>
  );
}

export default LoginPage;
